import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'motion/react';
import { cn } from './utils';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'gold' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Optional leading icon node. */
  leadingIcon?: ReactNode;
  /** Optional trailing icon node. */
  trailingIcon?: ReactNode;
  /** When true, the button stretches to fill its container. */
  fullWidth?: boolean;
  /** When true, applies the `btn-shine` sweep effect. Defaults to true for primary/gold. */
  shine?: boolean;
  /** Native button type — defaults to `button`. */
  type?: 'button' | 'submit' | 'reset';
};

const SIZE_CLS: Record<ButtonSize, string> = {
  sm: 'h-8 px-2.5 text-[12px] gap-1.5 rounded-full',
  md: 'h-10 px-3.5 text-[13px] gap-2 rounded-full',
  lg: 'h-12 px-5 text-[14px] gap-2 rounded-full',
};

const VARIANT_CLS: Record<ButtonVariant, string> = {
  primary:
    'bg-[rgba(66,53,33,0.48)] text-white backdrop-blur-[28px] backdrop-saturate-150 shadow-pop border border-transparent disabled:opacity-80',
  secondary:
    'bg-white/30 hover:bg-white/55 text-ink-800 border border-white/50 backdrop-blur-md disabled:opacity-80',
  ghost:
    'bg-white/30 hover:bg-white/55 text-ink-800 border border-white/50 backdrop-blur-md',
  gold: 'gold-gradient text-white shadow-gold border border-transparent disabled:opacity-80',
  danger:
    'bg-[rgba(200,90,90,0.12)] hover:bg-[rgba(200,90,90,0.2)] text-[#A03A3A] border border-[rgba(200,90,90,0.26)] disabled:opacity-80',
};

/**
 * The single Button primitive. Renders a `motion.button` so all callers get the
 * same press animation + shine sweep + focus-ring without re-implementing it.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    leadingIcon,
    trailingIcon,
    fullWidth,
    shine,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const wantsShine = shine ?? (variant === 'primary' || variant === 'gold');
  // Cast: motion.button accepts native props but its type intersection trips here.
  const motionProps: HTMLMotionProps<'button'> = { whileTap: { scale: 0.985 } };
  return (
    <motion.button
      ref={ref}
      type={type}
      {...motionProps}
      {...(rest as HTMLMotionProps<'button'>)}
      className={cn(
        'btn-press focus-ring inline-flex items-center justify-center font-semibold cursor-pointer',
        'transition-colors disabled:cursor-not-allowed',
        wantsShine && 'btn-shine',
        SIZE_CLS[size],
        VARIANT_CLS[variant],
        fullWidth && 'w-full',
        className,
      )}
    >
      {leadingIcon}
      {children}
      {trailingIcon}
    </motion.button>
  );
});
